import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Addresses, Addresstax } from "types";
import { phoneValidation, scheduledDate, priority } from 'validationSchemas/common'
import InstallationStatusTracker, { InstallStatus, RemovalStatus } from "components/InstallationStatusTracker";
import SectionHeader from "components/SectionPillPropsIcons";
import AddressFormModal from "components/AddAddressFormModal";
import InstructionsCreateCard from "components/Notes";
import ProductSelection from "assets/images/brands/ProductSelection.png"
import Doller from "assets/images/brands/doller.png"
import OrderNotes from "assets/images/brands/OrderNotes.png"
import StatusBadge from "components/Productlocationstatus";
import SpecialFeeui from "components/Tax/InstallationandremovalSpecialfeeui";
import PONumber from "components/PONumber";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
  applyRemovalResolveResponse,
  buildRemovalResolvePayload,
  getOrderDiscountFieldDisplay,
  mapInstallationSpecialFeeForResolve,
  mapInstallationSpecialFeeForSubmit,
  mapSavedInstallationSpecialFeeToUi,
  normalizeInstallationSelectedFee,
  overlayRemovalSubmitFromResolved,
  postRemovalOrderResolve,
} from "orderResolve";
import type { InstallationResolveAddress, ResolvedRemovalOrder } from "orderResolve";
// Call these ONLY when paymentStatus === 103 (parent confirm step)
import {
    createTaxCloudOrder,
    completeTaxCloudOrder,
} from "components/Tax/InstallationandRemovalTaxcloud"
// import { sendRemovalOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import { getAuthCookie } from "../../utils/authCookies";
import {
    buildRemovalDynamicCostAddressBody,
    buildRemovalAddressOptions,
    createRemovalOrder,
    mergeRemovalOrderBillingAddress,
    postRemovalDynamicCost,
    removalOrderUrls,
    resolveRemovalAddressMembershipId,
    resolveRemovalSellerOrganizationId,
    resolveRemovalSessionSellerOrganizationId,
    resolveRemovalSpecialFeePersonId,
    resolveRemovalSubmitBillingAddress,
    buildRemovalInstallerPayloadFields,
    resolveRemovalInstallerMembershipId,
    resolveRemovalInstallerName,
    resolveRemovalAgentMembershipId,
    resolveRemovalTeamMemberMembershipId,
    resolveRemovalBrokerageMembershipId,
    resolveRemovalSignageNotificationTarget,
} from "api/removalApi";
import { fetchUserAddresses as fetchMembershipAddresses, searchCompletedInstallationOrders } from "api/installationApi";
import { removalPartyIdentityFromSavedOrder } from "utils/removalPartyIdentity";
const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    border: "1px solid #dee2e6",          // Light border
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

/** Show a readable label for generated custom-product SKUs. */
function displayRemovalLineItemSku(item: { sku?: string; custom_sku?: string; inventory_source?: string }): string {
    if (String(item.inventory_source) !== "custom") return String(item.sku ?? "");

    const customSku = String(item.custom_sku ?? item.sku ?? "").trim();
    if (!customSku) return "";

    if (/^custom_sku(?:_|$)/i.test(customSku)) {
        return "Custom SKU";
    }

    return customSku;
}

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

// Select2 types for signage team members (installers)
type SignageTeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

// Select2 types for brokerage team members
type BrokerageTeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    offices: any[]; // Array of office objects from API
};

// Select2 types for team members
type TeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    offices: any[]; // Array of office objects from API
};

const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
        </components.DropdownIndicator>
    );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: 'flex !important',
        opacity: 1,
        visibility: 'visible',
    }),
    indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        display: 'none',
    }),
};
const SignageAddRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    const organizationId = getAuthCookie("organizationId");
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const licenseType = getAuthCookie("licenseType");
    const UNIQUE_ID_KEY1 = localStorage.getItem("re_signage_unique_id");
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [products, setProducts] = useState<any>(null);
    const [installer, setInstaller] = useState<any[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);

    // State for AsyncPaginate selected options
    const [selectedSignageTeamMemberOption, setSelectedSignageTeamMemberOption] = useState<SignageTeamMemberOption | null>(null);
    const [selectedBrokerageTeamMemberOption, setSelectedBrokerageTeamMemberOption] = useState<BrokerageTeamMemberOption | null>(null);
    const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<Selected | null>(null);
    const [selectedTeamMember, setSelectedTeamMember] = useState<Selected | null>(null);
    const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);
    const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    const [removalQuantities, setRemovalQuantities] = useState<Record<string, number>>({});
    const [quantityError, setQuantityError] = useState(false);
    const [removalCost, setRemovalCost] = useState<number | null>(null);
    const [hasClickedLocalLaws, setHasClickedLocalLaws] = useState(false);
    const [teamMembers, setTeamMembers] = useState<any[]>([]);
    const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [instructions, setInstructions] = useState<string>("");
    const [removalTrackingSummary, setRemovalTrackingSummary] = useState<any>(null);

    const sessionSellerOrgId = useMemo(
        () => resolveRemovalSessionSellerOrganizationId(),
        [organizationId]
    );

    const sellerOrgId = useMemo(
        () =>
            resolveRemovalSellerOrganizationId(removalOrder?.signage_id, removalOrder) ||
            resolveRemovalSessionSellerOrganizationId(),
        [removalOrder?.signage_id, removalOrder?.seller_org_id]
    );

    const allQuantitiesFilled = Object.values(removalQuantities).every(
        (val) => val !== undefined && val > 0
    );

    const [complietInstallationOrders, setComplietInstallationOrders] = useState<any[]>([]);

    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Addresses | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
        useState<Addresses | null>(null);

    const [installationAddress, setInstallationAddress] = useState<Addresses>();

    // state for banner below widget
    const [zeroOrNegativeMsg, setZeroOrNegativeMsg] = React.useState<string>("");
    const [dynamicCostId, setDynamicCostId] = React.useState<string>("");
    const [dynamicTaxable, setDynamicTaxable] = React.useState<boolean>(false);
    // wherever you render cost breakdown:
    // const [couponDiscounts, setCouponDiscounts] = React.useState<Discount[]>([]);
    const [showTaxDetails, setShowTaxDetails] = useState(false);
    const [couponInput, setCouponInput] = React.useState("");
    const [liveDiscountDisplay, setLiveDiscountDisplay] = React.useState({ code: "", amount: 0 });
    const lastResolvedRemovalRef = React.useRef<ResolvedRemovalOrder | null>(null);
    const userClearedCouponRef = React.useRef(false);

    // ✅ State for selected special fees (bypasses Formik timing issues)
    const [selectedSpecialFees, setSelectedSpecialFees] = useState<any[]>([]);
    const [allAvailableSpecialFees, setAllAvailableSpecialFees] = useState<any[]>([]);
    const [isRecomputing, setIsRecomputing] = useState(false);

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();
    const safeNavigate = createSafeNavigate(navigate);

    // Helper function for ID normalization
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

    // Pagination constant
    const PAGE_SIZE = 20;

    // ===== API: Signage Team Members with Select2 =====
    const loadSignageTeamMemberOptions: LoadOptions<SignageTeamMemberOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            const q = (inputValue || "").trim();

            try {
                const params = new URLSearchParams({
                    licenseType: "SIGNAGE_TEAM_MEMBER",
                    signageFirmID: organizationId || "",
                    page: String(page),
                    limit: String(PAGE_SIZE)
                });
                if (q) params.set("search", q);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    return { options: [], hasMore: false, additional: { page } };
                }

                const data = await response.json();
                const list = Array.isArray(data) ? data : data.data ?? data.results ?? [];

                const options: SignageTeamMemberOption[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                }));

                const hasMore = Array.isArray(data)
                    ? options.length === PAGE_SIZE
                    : data?.pagination?.more ??
                    (data?.total ? page * (data?.limit ?? PAGE_SIZE) < data.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                safeConsole.error("Failed to load signage team member options:", err);
                // Send error email notification
                await handleApiError(err, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_load_signage_team_member_options",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/organization-profiles/search`,
                    method: "GET",
                    additionalInfo: {
                        page,
                        search: q || "",
                        organizationId: organizationId || null,
                    }
                }, token);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [organizationId, token, userId, licenseType, userName]
    );

    // ===== API: Brokerage Team Members with Select2 =====
    const loadBrokerageTeamMemberOptions: LoadOptions<BrokerageTeamMemberOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            if (!removalOrder?.brokerage_firm_id) {
                return { options: [], hasMore: false, additional: { page } };
            }

            const q = (inputValue || "").trim();
            try {
                const params = new URLSearchParams({
                    licenseType: "BROKERAGE_TEAM_MEMBER",
                    brokerageFirmID: removalOrder.brokerage_firm_id,
                    page: String(page),
                    limit: String(PAGE_SIZE)
                });
                if (q) params.set("search", q);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },  
                });

                if (!response.ok) {
                    return { options: [], hasMore: false, additional: { page } };
                }

                const data = await response.json();
                const list = Array.isArray(data) ? data : data.data ?? data.results ?? [];

                const options: BrokerageTeamMemberOption[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                    offices: u?.offices || [],
                }));

                const hasMore = Array.isArray(data)
                    ? options.length === PAGE_SIZE
                    : data?.pagination?.more ??
                    (data?.total ? page * (data?.limit ?? PAGE_SIZE) < data.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                safeConsole.error("Failed to load brokerage team member options:", err);
                // Send error email notification
                await handleApiError(err, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_load_brokerage_team_member_options",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/users-search`,
                    method: "GET",
                    additionalInfo: {
                        page,
                        search: q || "",
                        brokerageFirmId: removalOrder?.brokerage_firm_id || null
                    }
                }, token);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [removalOrder?.brokerage_firm_id, token, userId, licenseType, userName]
    );

    // ===== API: Completed Installation Orders with Select2 (search + pagination) =====
    const loadCompletedInstallationOrderOptions: LoadOptions<{ value: string; label: string }, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            const searchEntityId = String(sessionSellerOrgId || organizationId || "").trim();
            if (!searchEntityId || !token) {
                return { options: [], hasMore: false, additional: { page } };
            }
            try {
                const params = new URLSearchParams({
                    q: (inputValue || "").trim(),
                    page: String(page),
                    limit: String(PAGE_SIZE),
                });

                const data = await searchCompletedInstallationOrders(
                    token,
                    searchEntityId,
                    params.toString()
                );

                const list = data?.results ?? data?.orders ?? data?.data ?? [];
                const options = (Array.isArray(list) ? list : []).map((o: any) => ({
                    value: o.id ?? o._id ?? "",
                    label: o.text ?? o.order_number ?? o.label ?? String(o.id ?? o._id ?? ""),
                }));

                const hasMore =
                    typeof data?.pagination?.more === "boolean"
                        ? !!data.pagination.more
                        : data?.totalPages
                        ? page < (data.totalPages || 1)
                        : data?.total
                        ? page * (data?.limit ?? PAGE_SIZE) < data.total
                        : options.length === PAGE_SIZE;

                return { options, hasMore, additional: { page: page + 1 } };
            } catch (err) {
                safeConsole.error("Failed to load completed installation orders:", err);
                await handleApiError(err, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_load_completed_installation_orders",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/installation_orders/search/${searchEntityId}`,
                    method: "GET",
                    additionalInfo: {
                        page,
                        search: (inputValue || "").trim() || "",
                        organizationId: searchEntityId,
                    }
                }, token);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [token, PAGE_SIZE, sessionSellerOrgId, organizationId, userId, licenseType, userName]
    );

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const addressOptions = useMemo(
        () => buildRemovalAddressOptions(addressObjects, removalOrder?.billing_address),
        [addressObjects, removalOrder?.billing_address]
    );

    const completedInstallationOrderOptions = complietInstallationOrders?.map(order => ({
        value: order._id,
        label: order.order_number,
    }));

    const teamMemberOptions = teamMembers?.map((member) => ({
        value: member.id,
        label: `${member.firstName} ${member.lastName}`,
        email: member.email,
        phone: member.phone1,
        offices: member.offices,
    }));

    // Fetch removal tracking summary
    const fetchRemovalTrackingSummary = async (orderId: string) => {
        try {
            if (!orderId) {
                // console.log("No installation order number available yet");
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/removal_tracking/installation_order/${orderId}/summary`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                safeConsole.error("Failed to fetch removal tracking summary:", data.error);
                return;
            }

            setRemovalTrackingSummary(data);
            // console.log("Fetched removal tracking summary:", data);
        } catch (err) {
            safeConsole.error("Error fetching removal tracking summary:", err);
            // Send error email notification
            await handleApiError(err, {
                module: "removal",
                action: "Signage_Add_Removal_Request_fetch_removal_tracking_summary",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal_orders/${orderId}/tracking-summary`,
                method: "GET",
                additionalInfo: {
                    orderId: orderId || null
                }
            }, token);
        }
    };

    // Helper function to get already removed quantity for a product
    const getAlreadyRemovedQuantity = (productId: string) => {
        if (!removalTrackingSummary) return 0;
        const summaryItem = removalTrackingSummary.find((item: any) =>
            item.key === productId && item.inventory_source !== "rotational"
        );
        return summaryItem ? summaryItem.total_removed : 0;
    };


    // Helper function to calculate available quantity for display
    const getAvailableQuantity = (item: any) => {
        const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
        return Math.max(0, item.quantity - alreadyRemoved);
    };


    // Update quantities when summary data changes
    useEffect(() => {
        if (removalOrder && removalTrackingSummary) {
            const updatedQuantities: Record<string, number> = {};
            removalOrder.line_items?.forEach((item: any) => {
                if (item.inventory_source !== "rotational") {
                    // Include regular + custom products that have available quantity > 0
                    const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                    const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                    if (availableQuantity > 0) {
                        updatedQuantities[item.product_id] = removalQuantities[item.product_id] || 0;
                    }
                }
            });
            setRemovalQuantities(updatedQuantities);
        }
    }, [removalTrackingSummary, removalOrder]);

    // const officesOptions = [
    //     { value: "office1", label: "Office 1" },
    //     { value: "office2", label: "Office 2" },
    //     { value: "office3", label: "Office 3" },
    // ];

    // const locationOptions = [
    //     { value: "new_york", label: "New York" },
    //     { value: "los_angeles", label: "Los Angeles" },
    //     { value: "chicago", label: "Chicago" },
    //     { value: "houston", label: "Houston" },
    // ];


    const fetchRemovalOrder = async (orderId: string | undefined) => {
        if (!orderId) return; // only fetch if id exists
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders/${orderId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error || "Failed to fetch removal order");
            }
            // console.log("Fetched installation Order:", data);
            setRemovalOrder(data);
            setProducts(data.line_items || []);

            // Initialize quantities after summary is loaded
            const initializeQuantities = async () => {
                await fetchRemovalTrackingSummary(data._id);

                const initialQuantities: Record<string, number> = {};
                data.line_items?.forEach((item: any) => {
                    if (item.inventory_source !== "rotational") {
                        // Include regular + custom products that have available quantity > 0
                        const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                        const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                        if (availableQuantity > 0) {
                            initialQuantities[item.product_id] = 0; // default quantity
                        }
                    }
                });
                setRemovalQuantities(initialQuantities);
            };

            initializeQuantities();
        } catch (error: any) {
            // showError(error.message || "Unexpected error while fetching removal order.");
            // Send error email notification
            await handleApiError(error, {
                module: "removal",
                action: "Signage_Add_Removal_Request_fetch_removal_order",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                method: "GET",
                additionalInfo: {
                    orderId: id || null
                }
            }, token);
        } finally {
            setLoading(false);
        }

    };

    useEffect(() => {
        if (id) {
            fetchRemovalOrder(id);
        }
    }, [id, token]);

    // Fetch addresses, set selected user, and populate states based on the order's user type when order is loaded
    useEffect(() => {
        if (!removalOrder) return;

        // Set installation/removal address
        const addr = removalOrder?.installation_details?.installation_address;
        setInstallationAddress(addr);

        // Set billing address based on inventory source
        if (removalOrder?.billing_address) {
            if (removalOrder.inventory_source === "office") {
                setSelectedOfficeBillingAddress(removalOrder.billing_address);
            } else {
                setSelectedBillingAddress(removalOrder.billing_address);
            }
            setAddressObjects((prev) =>
                mergeRemovalOrderBillingAddress(prev, removalOrder.billing_address)
            );
        }

        // Determine which user to set and fetch addresses for
        // Priority: office agent > agent > team member > foundation membership id
        let userIdForAddresses: string | null = null;

        // Check if this is an office agent order (has both agent_id and office)
        if (removalOrder.agent_id && removalOrder.office?._id) {
            userIdForAddresses = extractId(removalOrder.agent_id);

            // Set brokerage firm if available
            if (removalOrder.brokerage_firm_id && removalOrder.brokerage_firm_name) {
                const brokerageFirmOption = {
                    value: removalOrder.brokerage_firm_id,
                    label: removalOrder.brokerage_firm_name,
                    firstName: removalOrder.brokerage_firm_admin?.split(' ')[0] || "",
                    lastName: removalOrder.brokerage_firm_admin?.split(' ').slice(1).join(' ') || "",
                    email: "",
                    phone: "",
                };
                setSelectedBrokerageFirm(brokerageFirmOption);
            }

            // Set team member if available
            if (removalOrder.team_member_id && removalOrder.team_member_name) {
                const teamMemberOption = {
                    value: removalOrder.team_member_id,
                    label: removalOrder.team_member_name,
                    firstName: removalOrder.team_member_name.split(' ')[0] || "",
                    lastName: removalOrder.team_member_name.split(' ').slice(1).join(' ') || "",
                    email: removalOrder.user_email || "",
                    phone: removalOrder.user_phone || "",
                };
                setSelectedTeamMember(teamMemberOption);
            }

            // Set office agent option
            const agentOption = {
                value: extractId(removalOrder.agent_id),
                label: removalOrder.agent_name || "",
                firstName: removalOrder.agent_name?.split(' ')[0] || "",
                lastName: removalOrder.agent_name?.split(' ').slice(1).join(' ') || "",
                email: removalOrder.user_email || "",
                phone: removalOrder.user_phone || "",
            };
            setSelectedOfficeAgent(agentOption);

            // Also set the office
            const officeOption = {
                value: extractId(removalOrder.office._id),
                label: removalOrder.office.officeName || "",
                firstName: removalOrder.office.officeName || "",
                lastName: "",
                email: "",
                phone: "",
            };
            setSelectedOffice(officeOption);
        }
        // Check if this is a standalone agent order (has agent_id but no office)
        else if (removalOrder.agent_id && !removalOrder.office?._id) {
            userIdForAddresses = extractId(removalOrder.agent_id);

            // Set agent option
            const agentOption = {
                value: extractId(removalOrder.agent_id),
                label: removalOrder.agent_name || "",
                firstName: removalOrder.agent_name?.split(' ')[0] || "",
                lastName: removalOrder.agent_name?.split(' ').slice(1).join(' ') || "",
                email: removalOrder.user_email || "",
                phone: removalOrder.user_phone || "",
            };
            setSelectedAgent(agentOption);
        }
        // Check if this is a team member/office order (has team_member_id)
        else if (removalOrder.team_member_id) {
            // If order has an office but no agent, it's an office order
            if (removalOrder.office?._id && !removalOrder.agent_id) {
                userIdForAddresses = extractId(removalOrder.office._id);
            } else {
                // Otherwise use team member ID for address fetching
                userIdForAddresses = extractId(removalOrder.team_member_id);
            }

            // Set brokerage firm if available
            if (removalOrder.brokerage_firm_id && removalOrder.brokerage_firm_name) {
                const brokerageFirmOption = {
                    value: removalOrder.brokerage_firm_id,
                    label: removalOrder.brokerage_firm_name,
                    firstName: removalOrder.brokerage_firm_admin?.split(' ')[0] || "",
                    lastName: removalOrder.brokerage_firm_admin?.split(' ').slice(1).join(' ') || "",
                    email: "",
                    phone: "",
                };
                setSelectedBrokerageFirm(brokerageFirmOption);
            }

            // Set team member option
            const teamMemberOption = {
                value: removalOrder.team_member_id,
                label: removalOrder.team_member_name || "",
                firstName: removalOrder.team_member_name?.split(' ')[0] || "",
                lastName: removalOrder.team_member_name?.split(' ').slice(1).join(' ') || "",
                email: removalOrder.user_email || "",
                phone: removalOrder.user_phone || "",
            };
            setSelectedTeamMember(teamMemberOption);

            // If order has office, set it
            if (removalOrder.office?._id) {
                const officeOption = {
                    value: extractId(removalOrder.office._id),
                    label: removalOrder.office.officeName || "",
                    firstName: removalOrder.office.officeName || "",
                    lastName: "",
                    email: "",
                    phone: "",
                };
                setSelectedOffice(officeOption);
            }
        }

        userIdForAddresses =
            userIdForAddresses || resolveRemovalAddressMembershipId(removalOrder) || null;

        // Fetch addresses if we have a membership id and addresses aren't already loaded
        if (userIdForAddresses && addressObjects.length === 0) {
            fetchUserAddresses(userIdForAddresses);
        }
    }, [removalOrder, addressObjects.length]);

    // seting defult location state to show the poiner in map
    useEffect(() => {
        if (
            installationAddress?.address_details?.latitude &&
            installationAddress?.address_details?.longitude &&
            installationAddress?.address_details?.latitude !== 0 &&
            installationAddress?.address_details?.longitude !== 0
        ) {
            setSelectedLocation({
                lat: installationAddress?.address_details?.latitude,
                lng: installationAddress?.address_details?.longitude,
            });
        }
    }, [installationAddress]);

    // Signage team members are now loaded via AsyncPaginate

    // Brokerage team members are now loaded via AsyncPaginate

    // helper to extract possible id shapes into a string
    const extractId = (id: any): string => {
        if (!id) return "";
        if (typeof id === "string") return id;
        if (typeof id === "object") {
            return id._id || id.$oid || "";
        }
        return "";
    };

    // Build owner for AddressFormModal: prefer agent; else office
    const ownerOption = useMemo<{ value: string; label: string } | null>(() => {
        // Priority: office agent > agent > office
        if (selectedOfficeAgent) {
            return {
                value: selectedOfficeAgent.value,
                label: selectedOfficeAgent.label,
            };
        }
        if (selectedAgent) {
            return {
                value: selectedAgent.value,
                label: selectedAgent.label,
            };
        }
        if (selectedOffice) {
            return {
                value: selectedOffice.value,
                label: selectedOffice.label,
            };
        }
        return null;
    }, [selectedOfficeAgent, selectedAgent, selectedOffice]);

    const fetchUserAddresses = async (membershipId: string) => {
        if (!membershipId || !token) return;
        try {
            const data = await fetchMembershipAddresses(token, membershipId);
            const rows = Array.isArray(data) ? data : [];
            setAddressObjects(
                mergeRemovalOrderBillingAddress(rows, removalOrder?.billing_address)
            );
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
            setError(errorMessage);
            await handleApiError(err, {
                module: "removal",
                action: "Signage_Add_Removal_Request_fetch_user_addresses",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/organization-memberships/${membershipId}/addresses`,
                method: "GET",
                additionalInfo: {
                    membershipId,
                },
            }, token);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchRemovalCost = async () => {
            if (
                !installationAddress?.address_details?.latitude ||
                !installationAddress?.address_details?.longitude ||
                installationAddress?.address_details?.latitude === 0 ||
                installationAddress?.address_details?.longitude === 0
            ) {
                showError(
                    "Selected removal address is missing latitude/longitude. Please enter the address manually."
                );
                return;
            }

            const payload = buildRemovalDynamicCostAddressBody(
                installationAddress?.address_details
            );
            try {
                const data = await postRemovalDynamicCost(token, sellerOrgId, payload);
                setDynamicCostId(data.id);
                setDynamicTaxable(data.is_taxable || data.taxable);
                setRemovalCost(data.cost || 0);
            } catch (error: any) {
                setRemovalCost(null);
                const message = error instanceof Error ? error.message : "An unknown error occurred";
                showError(message);
                await handleApiError(error, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_fetch_removal_cost",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}${removalOrderUrls.dynamicCost(sellerOrgId)}`,
                    method: "POST",
                    requestBody: payload,
                    additionalInfo: {
                        sellerOrgId,
                        installationAddress: installationAddress ? "present" : "missing",
                    },
                }, token);
            }
        };

        if (installationAddress) {
            fetchRemovalCost();
        }
    }, [installationAddress, sellerOrgId, token, userId, licenseType, userName]);

    const DEFAULT_TEN_ZEROS = "0000000000";
    const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
        !v || v.trim() === "" || v === def ? def : v;

    const formik = useFormik({
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        initialValues: {
            userType: removalOrder?.user_type,
            userFirstName: "",
            userLastName: "",
            userEmail: removalOrder?.user_email || "",
            userPhone: removalOrder?.user_phone || "",
            brokerageFirmsId: removalOrder?.brokerage_firm_id,
            brokerage_firm_admin: removalOrder?.brokerage_firm_admin,
            teamMemberId: removalOrder?.team_member_id,
            teamMemberName: removalOrder?.team_member_name,
            office: removalOrder?.office,
            inventorySource: removalOrder?.inventory_source,
            order_number: removalOrder?.order_number,
            priority: "",
            removal_date: "",
            installation_order: "",
            assignedId: "",
            agentId: removalOrder?.agent_id,
            agent: removalOrder?.agent_name,
            officeId: removalOrder?.office?._id,
            removal_quantity: 0,
            billing_address: removalOrder?.billing_address,
            removal_address: removalOrder?.installation_details.installation_address,
            isSameAsBilling: !!removalOrder?.both_address_same,
            billingAddressId: removalOrder?.billing_address?._id,
            removalAddressId: removalOrder?.installation_details?.installation_address?._id,
            location: "",
            products: "",
            instructions: instructions,
            payment_method: "",
            po_number: "",
            image: null,
            localLawsChecked: false,
            subtotal: 0,
            total: 0,
            tax: 0,
            discount: 0,
            scheduled_date: "",
            jobs_today: 0,
            gatecode: "",
            label_of_address: "",
            firstName: "",
            lastName: "",
            phone: DEFAULT_TEN_ZEROS || "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zipcode: "",
            country: "",
            company: "",
            installation_cost: 0,
            install_taxable: true,
            install_tax_percentage: null,
            calc_install_rate_str: 0,
            calc_overall_rate_str: 0,
            calc_fee_rate_str: 0,
            destination: { addressLine1: "", city: "", state: "", zip: "", country: "US", stateCode: "", countryCode: "US" },
            specialfee: [] as Array<{
                id: string;
                name: string;
                amount: number;
                taxable: boolean;
                tax_percentage?: number | null;
                tax_amount?: number | null;
            }>,
            totals: { installation_subtotal: 0, installation_tax_amount: 0, specialfee_subtotal: 0, specialfee_tax_amount: 0, grand_total: 0, cartId: "", specialfee_id: [], specialfee_name: [] },
            discount_amount: 0,
            discount_code: "",
            tax_status: "",
        },
        validationSchema: Yup.object({
            assignedId: Yup.string().required("Assign to is required"),
            removal_date: Yup.string().notRequired(),
            // installation_order: Yup.string()
            //     .required("Installation job order is required"),
            // scheduled_date: scheduledDate,
            // ✅ Required dropdown for Removal Priority
            priority: priority,

            // ✅ Billing address required
            billingAddressId: Yup.string()
                .required("Billing address is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //     "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
            userEmail: Yup.string().email("Invalid email").required("Email is required"),
            userPhone: phoneValidation,
        }),
        onSubmit: async (values) => {
            // ✅ Validate required special fees BEFORE submission
            const requiredFees = allAvailableSpecialFees.filter((f: any) => f.required === true);
            const selectedFeeIds = selectedSpecialFees.map((f: any) => String(f.id));
            const missingRequiredFees = requiredFees.filter((f: any) => !selectedFeeIds.includes(String(f.id || f._id)));

            if (missingRequiredFees.length > 0) {
                const feeNames = missingRequiredFees.map((f: any) => f.name).join(", ");
                showError(`The following required special fees must be selected: ${feeNames}`);
                return; // Don't submit
            }

            if (
                !selectedInstaller?.value ||
                !installationAddress?.address_details ||
                Number.isNaN(Number(removalCost))
            ) {
                showError("Complete installer and removal address before saving.");
                return;
            }

            setFormSubmitting(true);
            let removalOrderPayload: Record<string, unknown> | undefined;
            try {
                const resolveOk = await recompute({ force: true });
                if (!resolveOk || !lastResolvedRemovalRef.current) {
                    showError("Pricing could not be calculated. Please try again.");
                    return;
                }

                const resolved = lastResolvedRemovalRef.current;
                const uiFees = selectedSpecialFees || [];
                const feeRows = uiFees.map((f) =>
                    mapInstallationSpecialFeeForSubmit(f, { catalog: allAvailableSpecialFees })
                );
                const billingAddress = resolveRemovalSubmitBillingAddress({
                    billingAddressId: values.billingAddressId,
                    inventorySource: removalOrder?.inventory_source,
                    addressObjects,
                    selectedBillingAddress,
                    selectedOfficeBillingAddress,
                    orderBillingAddress: removalOrder?.billing_address,
                    formBillingAddress: values.billing_address,
                });

                const imageUrl = values.image ? await uploadFile(values.image) : null;
                const cleanedLineItems = products?.filter((item: any) => {
                    if (item.inventory_source === "rotational") {
                        // Include all rotational products in payload but don't show in UI
                        return true;
                    } else if (item.inventory_source === "custom") {
                        // Include custom products in payload (qty to remove from user input)
                        return true;
                    } else {
                        // For other non-rotational products, only include if there's remaining quantity
                        const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                        const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                        return availableQuantity > 0;
                    }
                }).map((item: any) => {
                    const toRemove = removalQuantities[item.product_id] || 0;
                    const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                    const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);

                    // Simple serial allocations logic - send all rotational products as-is
                    let serial_allocations = [];

                    if (item.inventory_source !== "rotational") {
                        // For normal products, find all serials where source_product_id === item.product_id
                        serial_allocations = products
                            .filter((p: any) => p.inventory_source === "rotational" && Array.isArray(p.serial_allocations))
                            .flatMap((p: any) =>
                                p.serial_allocations
                                    .filter((s: any) => s.source_product_id === item.product_id)
                                    .map((s: any) => ({
                                        bin_id: s.bin_id,
                                        rack_id: s.rack_id,
                                        serial_id: s.serial_id,
                                        serial_code: s.serial_code,
                                        warehouse_id: s.warehouse_id,
                                        status: s.status,
                                        zone_id: s.zone_id,
                                        warehouse_name: s.warehouse_name,
                                        zone_name: s.zone_name,
                                        rack_name: s.rack_name,
                                        bin_name: s.bin_name,
                                    }))
                            );
                    } else if (Array.isArray(item.serial_allocations)) {
                        // For rotational products, send all serials as-is
                        serial_allocations = item.serial_allocations?.map((s: any) => ({
                            bin_id: s.bin_id,
                            rack_id: s.rack_id,
                            serial_id: s.serial_id,
                            serial_code: s.serial_code,
                            warehouse_id: s.warehouse_id,
                            status: s.status,
                            zone_id: s.zone_id,
                            warehouse_name: s.warehouse_name,
                            zone_name: s.zone_name,
                            rack_name: s.rack_name,
                            bin_name: s.bin_name,
                        }));
                    }

                    return {
                        product_id: item.product_id,
                        variant_id: item.variant_id || "",
                        product_name: item.product_name,
                        inventory_source: item.inventory_source,
                        rotational_allocated: item.rotational_allocated,
                        source_product_id: item.inventory_source === "rotational" ? item.serial_allocations[0].source_product_id
                            : item.product_id,
                        sku: item.sku,
                        available_quantity: availableQuantity - toRemove,
                        quantity_to_be_removed: toRemove,
                        restock_quantity: 0,
                        writeoff_quantity: 0,
                        lost_quantity: 0,
                        reason_to_writeoff: "",
                        current_status: item.current_status,
                        unit_price: item.unit_price,
                        total_price: item.total_price,
                        discount: item.discount || 0,
                        tax_amount: 0,
                        is_rotational: item.is_rotational,
                        notes: [
                            {
                                value: values.instructions,
                                added_by: {
                                    id: userId,
                                    name: userName,
                                },
                                added_at: new Date().toISOString(),
                            },
                        ],
                        storage_locations: item.storage_locations || [],
                        serial_allocations,
                    };
                });


                removalOrderPayload = {
                    // Construct your payload here based on form values + local state
                    user_type: values.userType,
                        user_email: values.userEmail,
                        user_name: values.userFirstName + " " + values.userLastName,
                        user_phone: values.userPhone,
                        order_number: null, // or generate dynamically
                        status: 100,
                        installation_order_id: removalOrder?._id,
                        installation_order_number: removalOrder?.order_number,
                        inventory_source: values.inventorySource,
                        source_id: removalOrder?.source_id,
                        brokerage_firm_id: removalOrder?.brokerage_firm_id || undefined,
                        brokerage_firm_name: removalOrder?.brokerage_firm_name || undefined,
                        brokerage_firm_admin: removalOrder?.brokerage_firm_admin || undefined,
                        team_member_id: values.teamMemberId || undefined,
                        team_member_name: values.teamMemberName || undefined,
                        office: removalOrder?.office || undefined,
                        agent_id: values.agentId || undefined,
                        agent_name: removalOrder?.agent_name || undefined,
                        signage_id: resolveRemovalSellerOrganizationId(
                            removalOrder?.signage_id,
                            removalOrder
                        ) || resolveRemovalSessionSellerOrganizationId(),
                        signage_company_name: removalOrder?.signage_company_name || "",
                        ...removalPartyIdentityFromSavedOrder(removalOrder),
                        line_items: cleanedLineItems,
                        customer_group_id: null,
                        payment_method: values.payment_method,
                        payment_status: "Pending",
                        po_number: String(values.po_number || "").trim(),
                        payment_info: [], // or provide details
                        gate_access_code: values.gatecode || "",
                        removal_details: {
                            ...(values.removal_date
                                ? { scheduled_date: new Date(values.removal_date).toISOString() }
                                : {}),
                            removal_address: removalOrder?.installation_details.installation_address,
                            ...buildRemovalInstallerPayloadFields(removalOrder, selectedInstaller, {
                                contactNumber: selectedInstaller?.phone,
                            }),
                            estimated_duration: null,
                            actual_removal_date: null,
                            additional_instructions: [
                                {
                                    value: values.instructions || "",
                                    added_by: {
                                        id: userId,
                                        name: userName,
                                    },
                                    added_at: new Date().toISOString(),
                                },
                            ],
                            tax_details: null,
                        },
                        billing_address: billingAddress,
                        both_address_same: values.isSameAsBilling,
                        additional_instructions: [
                            {
                                value: values.instructions || "",
                                added_by: {
                                    id: userId,
                                    name: userName,
                                },
                                added_at: new Date().toISOString(),
                            },
                        ],
                        local_laws_compliance: values.localLawsChecked,
                        priority: Number(values.priority),
                        uploaded_files: uploadedFileMeta || [],

                        // Financial fields — source of truth is last /orders/resolve response only
                        subtotal_amount: Number(resolved.subtotal_amount ?? 0),
                        removal_cost: Number(resolved.removal_cost ?? removalCost ?? 0),
                        specialfee: feeRows,
                        additional_cost: Number(resolved.additional_cost ?? 0),
                        discount_amount: Number(resolved.discount_amount ?? 0),
                        discount_code: String(resolved.discount_code ?? ""),

                        total_amount: Number(resolved.total_amount ?? 0),

                        tax_status: String(resolved.tax_status ?? ""),
                        removal_tax_amount: Number(resolved.removal_tax_amount ?? 0),
                        removal_tax_percentage: resolved.removal_tax_percentage ?? undefined,
                        over_all_tax_amount: Number(resolved.over_all_tax_amount ?? 0),
                        over_all_tax_percentage: resolved.over_all_tax_percentage ?? "",
                        tax_inclusive: false,
                        cart_id: resolved.cart_id || UNIQUE_ID_KEY1 || undefined,
                        version: 1,
                        tax_details: null,
                };

                removalOrderPayload = overlayRemovalSubmitFromResolved(
                    removalOrderPayload,
                    lastResolvedRemovalRef.current
                ) as Record<string, unknown>;

                const response = await createRemovalOrder(token, removalOrderPayload);
                const data = await response.json();
                if (!response.ok) {
                    showError(data.error || "Failed to create installation");
                    throw new Error(data.error || "Failed to create installation");
                }

                // Email moved to backend (removal_order_email_service)
                // try {
                //     const createdRemovalOrderId = String(
                //         (data as any)?.order_id || (data as any)?._id || (data as any)?.id || ""
                //     ).trim();
                //     if (createdRemovalOrderId && token) {
                //         await sendRemovalOrderTriggerEmail({
                //             triggerName: "Signage Removal request created",
                //             removalOrderId: createdRemovalOrderId,
                //             token,
                //             includeOrderPdfAttachment: true,
                //         });
                //     }
                // } catch (emailError) {
                //     safeConsole.warn("Failed to send removal request creation emails:", emailError);
                // }

                // ✅ Trigger: Removal Request Created - Send notifications
                try {
                    const { createNotification } = await import("../../services/notificationApi");
                    const { getRemovalRequestCreatedContent } = await import("../../services/notificationContent");
                    const { fetchRemovalMembershipContact, removalSessionContactFromCookies } = await import("../../api/removalApi");
                    
                    const orderId = `#${data.order_number || data.order_id || "REM-XXXX"}`;
                    const signageCompanyName = userName || "Signage Company";
                    const customerName = removalOrder?.agent_name || removalOrder?.team_member_name || removalOrder?.brokerage_firm_name || "Customer";

                    // Resolve single end-user for external notifications: prefer Agent > Team Member > Brokerage
                    const hasAgentRecipient = !!removalOrder?.agent_id;
                    const hasTeamMemberRecipient = !!(removalOrder?.team_member_id && removalOrder?.brokerage_firm_id && removalOrder.team_member_id !== removalOrder.brokerage_firm_id);
                    const hasBrokerageRecipient = !!removalOrder?.brokerage_firm_id;
                    
                    // Send notification to Signage Admin/Team Member (creator)
                    const currentUserId = getAuthCookie("userId");
                    if (currentUserId && token) {
                        try {
                            const currentUserDataForNotif = removalSessionContactFromCookies() ?? await fetchRemovalMembershipContact(token, getAuthCookie("membershipId") || currentUserId);
                            if (currentUserDataForNotif?.id) {
                                const signageContent = getRemovalRequestCreatedContent({
                                    orderId: orderId,
                                    createdBy: customerName,
                                    recipientType: currentUserDataForNotif?.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin",
                                });
                                await createNotification(currentUserId, signageContent, token || "", currentUserDataForNotif?.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin");
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send removal request notification to signage admin:", err);
                        }
                    }

                    // Also notify Assigned To signage team member (installer) - always send
                    if (selectedInstaller?.value && token) {
                        try {
                            const installerContent = getRemovalRequestCreatedContent({
                                orderId: orderId,
                                createdBy: customerName,
                                recipientType: "team_member",
                            });
                            await createNotification(selectedInstaller.value, installerContent, token || "", "team_member");
                        } catch (err) {
                            safeConsole.warn("Failed to send removal request notification to assigned installer:", err);
                        }
                    }
                    
                    // Send notification to Brokerage Firm Admin/Team Member (only if no agent or team member end user)
                    const brokerageMembershipId = resolveRemovalBrokerageMembershipId(removalOrder);
                    if (!hasAgentRecipient && !hasTeamMemberRecipient && hasBrokerageRecipient && brokerageMembershipId && token) {
                        try {
                            const brokerageData = await fetchRemovalMembershipContact(token, brokerageMembershipId);
                            if (brokerageData?.id) {
                                const brokerageContent = getRemovalRequestCreatedContent({
                                    orderId: orderId,
                                    signageCompanyName: signageCompanyName,
                                    recipientType: "brokerage_team",
                                });
                                await createNotification(brokerageMembershipId, brokerageContent, token || "", "brokerage_team");
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send removal request notification to brokerage firm:", err);
                        }
                    }
                    
                    // Send notification to Team Member (if different from brokerage, and no agent end user)
                    const teamMemberMembershipId = resolveRemovalTeamMemberMembershipId(removalOrder);
                    if (!hasAgentRecipient && hasTeamMemberRecipient && teamMemberMembershipId && removalOrder?.brokerage_firm_id && teamMemberMembershipId !== resolveRemovalBrokerageMembershipId(removalOrder) && token) {
                        try {
                            const teamMemberData = await fetchRemovalMembershipContact(token, teamMemberMembershipId);
                            if (teamMemberData?.id) {
                                const teamMemberContent = getRemovalRequestCreatedContent({
                                    orderId: orderId,
                                    signageCompanyName: signageCompanyName,
                                    recipientType: "brokerage_team",
                                });
                                await createNotification(teamMemberMembershipId, teamMemberContent, token || "", "brokerage_team");
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send removal request notification to team member:", err);
                        }
                    }
                    
                    // Send notification to Agent (preferred end user)
                    const agentMembershipId = resolveRemovalAgentMembershipId(removalOrder);
                    if (hasAgentRecipient && agentMembershipId && token) {
                        try {
                            const agentData = await fetchRemovalMembershipContact(token, agentMembershipId);
                            if (agentData?.id) {
                                const agentContent = getRemovalRequestCreatedContent({
                                    orderId: orderId,
                                    supplierName: signageCompanyName,
                                    recipientType: "agent",
                                });
                                await createNotification(agentMembershipId, agentContent, token || "", "agent");
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send removal request notification to agent:", err);
                        }
                    }
                } catch (notifError) {
                    safeConsole.warn("Failed to send removal request creation notifications:", notifError);
                }

                safeNavigate(`${baseRoute}/jobs/removalWorkorder/${data.order_id}`);
                showSuccess("Removal request created successfully!");
                localStorage.removeItem("re_signage_unique_id");
            } catch (error: any) {
                safeConsole.error(error);
                showError(`Error: ${error.message}`);
                // Send error email notification
                await handleApiError(error, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_create_removal_order",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}${removalOrderUrls.orders()}`,
                    method: "POST",
                    requestBody: removalOrderPayload,
                    additionalInfo: {
                        hasProducts: (formik.values.products && formik.values.products.length > 0) || false,
                        hasAddress: !!installationAddress,
                        buyerType: formik.values.brokerageFirmsId ? "brokerageFirm" : "agent"
                    }
                }, token);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    const { values, setFieldValue } = formik;

    // Legacy subtotal/total fields — synced from resolve totals only (no installation-order math).
    React.useEffect(() => {
        const t = formik.values.totals || {};
        const subAll = Number(t.installation_subtotal || 0) + Number(t.specialfee_subtotal || 0);
        const taxAll = Number(t.installation_tax_amount || 0) + Number(t.specialfee_tax_amount || 0);
        const grand = Number(t.grand_total || 0);

        formik.setFieldValue("subtotal", subAll, false);
        formik.setFieldValue("tax", taxAll, false);
        formik.setFieldValue("total", Math.max(0, grand - Number(formik.values.discount_amount || 0)), false);
    }, [
        formik.values.totals.installation_subtotal,
        formik.values.totals.specialfee_subtotal,
        formik.values.totals.installation_tax_amount,
        formik.values.totals.specialfee_tax_amount,
        formik.values.totals.grand_total,
        formik.values.discount_amount,
    ]);

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "removal_requests");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200 || xhr.status === 201) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    // to get the scheduled job count based on the selected date
    useEffect(() => {
        const fetchScheduledJobs = async () => {
            if (!formik.values.scheduled_date || !userId) return;

            const date = new Date(formik.values.scheduled_date).toISOString().split("T")[0]; // format as YYYY-MM-DD

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.assignedId}/${date}/scheduled_jobs`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Failed to fetch job count");
                // console.log("Scheduled Jobs:", data);

                setScheduledJobsCount(data.scheduled_jobs); // assuming response shape: { count: number }
                formik.setFieldValue("scheduled_jobs", data.scheduled_jobs);
            } catch (err: any) {
                safeConsole.error("Scheduled Jobs Error:", err.message);
                setScheduledJobsCount(null);
                // Send error email notification
                await handleApiError(err, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_fetch_scheduled_jobs",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.assignedId}/${date}/scheduled_jobs`,
                    method: "GET",
                    additionalInfo: {
                        assignedId: formik.values.assignedId || null,
                        scheduledDate: date || null
                    }
                }, token);
            }
        };

        fetchScheduledJobs();
    }, [formik.values.assignedId, userId, token]);

    const handleSaveInstructions = (newInstructions: string) => {
        setInstructions(newInstructions);
        formik.setFieldValue("instructions", newInstructions);
    };



    // tax related code 


    // --- unique draft id for "add installation" ---
    const UNIQUE_ID_KEY = "re_signage_unique_id";

    async function ensureDraftUniqueId(token?: string): Promise<string | null> {
        try {
            // 1) if already cached, return it
            const existing = localStorage.getItem(UNIQUE_ID_KEY);
            if (existing) return existing;

            if (!token) return null;

            // 2) else fetch one from API and cache it
            const res = await fetch(`${process.env.REACT_APP_API_URL}/utils/new-unique-id`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!res.ok) return null;

            const data = await res.json().catch(() => ({}));
            const id = data?.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
            if (!id) return null;

            localStorage.setItem(UNIQUE_ID_KEY, id);
            // console.log("Generated new draft unique ID:", id);
            return id;
        } catch {
            return null;
        }
    }

    // ✅ Call ensureDraftUniqueId when buyer type is set (from removalOrder or programmatically)
    React.useEffect(() => {
        const currentType = formik.values.userType;
        if (!currentType || !token) return;

        const ensureId = async () => {
            try {
                const id = await ensureDraftUniqueId(token || "");
                if (id) {
                    formik.setFieldValue("draft_unique_id", id, false);
                }
            } catch (err) {
                safeConsole.error("Error ensuring draft unique ID:", err);
                // Send error email notification
                await handleApiError(err, {
                    module: "removal",
                    action: "Signage_Add_Removal_Request_ensure_draft_unique_id",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/removal_orders/draft/ensure-unique-id`,
                    method: "POST"
                }, token);
            }
        };

        ensureId();
    }, [formik.values.userType, token]);


    const coalesceDestination = (): Addresstax => {
        const a: Addresstax | undefined = installationAddress?.address_details;
        // const f: Address | undefined = formik.values?.destination;

        return {
            addressLine1: a?.addressLine1 ?? "",
            city: a?.city ?? "",
            state: a?.stateCode ?? a?.state ?? "",
            zip: a?.zip ?? "",
            // country: a?.country ??  "US",
            stateCode: a?.stateCode ?? a?.state ?? "",
            // countryCode: a?.countryCode ?? "US",
        };
    };


    // ✅ Simple: Just use the state directly (no Formik reads!)
    const getSelectedFees = () => {
        return selectedSpecialFees
            .map((f) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
            .filter((f) => f.id !== "");
    };

    const supplierId = String(selectedInstaller?.value ?? "").trim();

    const currentOwner =
        removalOrder?.agent_id
            ? { id: removalOrder.agent_id, name: removalOrder.agent_name, type: "agent" as const }
            : removalOrder?.office?._id
                ? { id: removalOrder.office._id, name: removalOrder.office.officeName, type: "office" as const }
                : null;

    // ✅ Separate logic for CouponDiscount customerId (different from currentOwner)
    const effectiveCouponCreatedById = React.useMemo(
        () => resolveRemovalSpecialFeePersonId(removalOrder, { sessionMembershipId: userId }),
        [removalOrder, userId]
    );
    const removalSpecialFeeSupplierId = React.useMemo(
        () =>
            resolveRemovalSellerOrganizationId(removalOrder?.signage_id, removalOrder) ||
            sessionSellerOrgId,
        [removalOrder, sessionSellerOrgId]
    );

    // ✅ Add ref to prevent overlapping recompute calls
    const isRecomputingRef = React.useRef(false);
    const isUpdatingFromRecomputeRef = React.useRef(false);
    const lastRecomputeParamsRef = React.useRef("");
    const recomputeRequestIdRef = React.useRef(0);
    const pendingRecomputeRef = React.useRef(false);
    const formikValuesRef = React.useRef(formik.values);

    React.useEffect(() => {
        formikValuesRef.current = formik.values;
    }, [formik.values]);

    // ✅ Accept override to avoid reading stale state
    type RecomputeOverride = { fees?: any[]; discountCode?: string; force?: boolean };

    const recompute = React.useCallback(async (override?: RecomputeOverride) => {
        if (!dynamicCostId || !token) return false;
        if (isRecomputingRef.current && !override?.force) return false;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        const fees = override?.fees ?? getSelectedFees();
        const allFeesSubtotal = fees.reduce((sum, f) => sum + Number(f.amount || 0), 0);
        const discountCode =
            override?.discountCode !== undefined
                ? String(override.discountCode).trim()
                : String(
                    userClearedCouponRef.current
                        ? ""
                        : formikValuesRef.current.discount_code || couponInput
                ).trim();

        const addr = installationAddress?.address_details;
        const requestSignature = JSON.stringify({
            dynamicCostId,
            removalCost: Number(removalCost) || 0,
            dynamicTaxable,
            discountCode,
            feeSig: fees.map((f) => `${f.id}:${Number(f.amount || 0)}`).sort().join(","),
            installer: selectedInstaller?.value || "",
            addressSig: addr
                ? `${addr.addressLine1}-${addr.city}-${addr.state}-${addr.zip}`
                : "",
        });

        if (!override?.force && lastRecomputeParamsRef.current === requestSignature) {
            return true;
        }

        recomputeRequestIdRef.current += 1;
        const currentRequestId = recomputeRequestIdRef.current;
        lastRecomputeParamsRef.current = requestSignature;

        isRecomputingRef.current = true;
        setIsRecomputing(true);
        try {
            if (currentRequestId !== recomputeRequestIdRef.current) return false;

            const billingAddress = resolveRemovalSubmitBillingAddress({
                billingAddressId: formikValuesRef.current.billingAddressId,
                inventorySource: removalOrder?.inventory_source,
                addressObjects,
                selectedBillingAddress,
                selectedOfficeBillingAddress,
                orderBillingAddress: removalOrder?.billing_address,
                formBillingAddress: formikValuesRef.current.billing_address,
            });

            const resolvePayload = buildRemovalResolvePayload({
                cartId: String(UNIQUE_ID_KEY1 || formik.values.totals?.cartId || ""),
                dynamicCostId,
                signageId: sellerOrgId,
                sourceId: String(removalOrder?.source_id || ""),
                customerId: String(effectiveCouponCreatedById || removalOrder?.source_id || ""),
                inventorySource: String(removalOrder?.inventory_source || "agent"),
                removalCost: Number(removalCost) || 0,
                removalTaxable: dynamicTaxable,
                discountCode,
                paymentMethod: formikValuesRef.current.payment_method || "card",
                userType: formikValuesRef.current.userType,
                userName: `${formikValuesRef.current.userFirstName || ""} ${formikValuesRef.current.userLastName || ""}`.trim(),
                userEmail: formikValuesRef.current.userEmail,
                userPhone: formikValuesRef.current.userPhone,
                brokerageFirmId: removalOrder?.brokerage_firm_id ?? null,
                brokerageFirmName: removalOrder?.brokerage_firm_name ?? null,
                brokerageFirmAdmin: removalOrder?.brokerage_firm_admin ?? "",
                teamMemberId: formikValuesRef.current.teamMemberId || removalOrder?.team_member_id || null,
                teamMemberName: formikValuesRef.current.teamMemberName || removalOrder?.team_member_name || "",
                office: removalOrder?.office,
                agentId: formikValuesRef.current.agentId || removalOrder?.agent_id || null,
                agentName: removalOrder?.agent_name || null,
                billingAddress: billingAddress as InstallationResolveAddress | null,
                removalAddress: installationAddress as InstallationResolveAddress | null,
                installerId: resolveRemovalInstallerMembershipId(removalOrder, selectedInstaller),
                installerName: resolveRemovalInstallerName(removalOrder, selectedInstaller),
                lineItems: [],
                specialFees: fees.map((f) => mapInstallationSpecialFeeForResolve(f, allAvailableSpecialFees)),
                partyIdentity: removalPartyIdentityFromSavedOrder(removalOrder),
            });

            const resolved = await postRemovalOrderResolve(token, {
                order_type: "removal",
                action: "draft",
                payload: resolvePayload,
            });

            if (currentRequestId !== recomputeRequestIdRef.current) return false;

            if (!resolved || typeof resolved !== "object") {
                formik.setFieldValue("totals.installation_subtotal", Number(removalCost) || 0, false);
                formik.setFieldValue("totals.installation_tax_amount", 0, false);
                formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
                formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
                formik.setFieldValue("totals.grand_total", (Number(removalCost) || 0) + allFeesSubtotal, false);
                formik.setFieldValue("specialfee", fees, false);
                return false;
            }

            lastResolvedRemovalRef.current = resolved;
            isUpdatingFromRecomputeRef.current = true;
            const applied = applyRemovalResolveResponse(
                resolved,
                formik,
                Number(removalCost) || 0,
                {
                    requestedDiscountCode: discountCode,
                    existingFeeRows: fees,
                    specialFeeCatalog: allAvailableSpecialFees,
                }
            );
            setSelectedSpecialFees(applied.mergedFees);
            formik.setFieldValue("specialfee", applied.mergedFees, false);
            setLiveDiscountDisplay(
                getOrderDiscountFieldDisplay({
                    formikCode: formikValuesRef.current.discount_code,
                    formikAmount: formikValuesRef.current.discount_amount,
                    couponInput,
                    resolved: resolved as Parameters<typeof getOrderDiscountFieldDisplay>[0]["resolved"],
                })
            );
            if (discountCode) {
                setCouponInput(discountCode);
            } else if (!formikValuesRef.current.discount_code) {
                setCouponInput("");
            }
            return true;
        } catch (e) {
            safeConsole.error("[OrderResolve] removal failed:", e);
            return false;
        } finally {
            isRecomputingRef.current = false;
            setIsRecomputing(false);
            if (pendingRecomputeRef.current) {
                pendingRecomputeRef.current = false;
                setTimeout(() => {
                    void recompute({ force: true });
                }, 0);
            }
        }
    }, [
        token,
        dynamicCostId,
        removalCost,
        dynamicTaxable,
        couponInput,
        sellerOrgId,
        removalOrder,
        selectedInstaller?.value,
        selectedInstaller?.label,
        selectedBillingAddress,
        selectedOfficeBillingAddress,
        installationAddress,
        effectiveCouponCreatedById,
        getSelectedFees,
        formik,
    ]);

    // keep these fields in sync with engine totals


    const ready = React.useMemo(
        () =>
            Boolean(
                selectedInstaller?.value &&
                installationAddress?.address_details &&
                !Number.isNaN(removalCost)
            ),
        [selectedInstaller?.value, installationAddress?.address_details, removalCost]
    );

    // ---- Debounced trigger (runs only when ready) ----------------------
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerRecomputeIfReady = React.useCallback(() => {
        if (!ready) return;

        if (isRecomputingRef.current) {
            pendingRecomputeRef.current = true;
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (!isRecomputingRef.current) {
                recompute();
            }
        }, 300);
    }, [ready, recompute]);

    // optional: clear any pending timer on unmount
    React.useEffect(() => {
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, []);

    // ---- Watch prerequisites + address/cost (not fee enrichment) ----
    React.useEffect(() => {
        if (isRecomputingRef.current) return;
        triggerRecomputeIfReady();
    }, [
        selectedInstaller?.value,
        installationAddress?.address_details?.addressLine1,
        installationAddress?.address_details?.city,
        installationAddress?.address_details?.state,
        installationAddress?.address_details?.zip,
        removalCost,
        dynamicCostId,
        dynamicTaxable,
        formik.values.discount_code,
        triggerRecomputeIfReady,
    ]);



    // Only if you HAVE these values in this component:
    const [paymentStatus, setPaymentStatus] = React.useState<number>(0);
    const [orderId, setOrderId] = React.useState<string>("");
    const [selectedInstallationOption, setSelectedInstallationOption] = useState<{ value: string; label: string } | null>(null);

    React.useEffect(() => {
        const cartId = formik.values?.totals?.cartId;
        if (paymentStatus !== 103 || !cartId || !orderId) return;

        let cancelled = false;
        (async () => {
            try {
                await createTaxCloudOrder(token || "", cartId, orderId, supplierId);
                if (cancelled) return;
                await completeTaxCloudOrder(token || "", orderId, supplierId, new Date().toISOString());
            } catch (e) {
                safeConsole.error("[TaxCloud finalize] failed:", e);
            }
        })();

        return () => { cancelled = true; };
    }, [paymentStatus, formik.values?.totals?.cartId, orderId, token]);

    const prevFeesRef = React.useRef<string>("");
    React.useEffect(() => {
        if (isRecomputingRef.current) return;
        if (isUpdatingFromRecomputeRef.current) {
            isUpdatingFromRecomputeRef.current = false;
            return;
        }

        const feeSig = selectedSpecialFees
            .map((f) => `${f.id}:${Number(f.amount || 0)}`)
            .sort()
            .join(",");
        if (prevFeesRef.current === feeSig) return;
        prevFeesRef.current = feeSig;

        if (!ready) return;
        triggerRecomputeIfReady();
    }, [selectedSpecialFees, ready, triggerRecomputeIfReady]);

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content container">
            <div className="page-container mt-3">
                <h3 className="m-0 text-center fw-bold addheaders">Add Removal Request</h3>
            </div>
            <div className="my-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap col-lg-12 os-card" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', borderRadius: '12px', padding: '20px', backgroundColor: 'white' }}>
                    {/* Left: Removal Status Tracker */}
                    <div className="flex-grow-1">
                        <InstallationStatusTracker
                            type="removal"
                            status={0}
                            className="w-100"
                            organizationId={organizationId || ""}
                        />
                    </div>
                </div>
            </div>
            <form onSubmit={formik.handleSubmit} className="me-2 me-lg-0">
                <div className="row p-3 col-lg-12 card-body shadow-lg pb-4 mb-4 ms-0">
                    <div className="mb-3">
                        <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
                    </div>

                    <div className="col-lg-3 mb-3">
                        <label className="form-label table-cells">
                            Installation Job
                        </label>
                        <AsyncPaginate
                            id="installation_order"
                            name="installation_order"
                            // isClearable
                            value={
                                selectedInstallationOption ||
                                (formik.values.installation_order
                                    ? { value: formik.values.installation_order, label: formik.values.order_number }
                                    : null)
                            }
                            loadOptions={loadCompletedInstallationOrderOptions}
                            onChange={async (selectedOption: any) => {
                                if (selectedOption) {
                                    setSelectedInstallationOption({ value: selectedOption.value, label: selectedOption.label });
                                    setSelectedSpecialFees([]);
                                    formik.setFieldValue("specialfee", [], false);
                                    formik.setFieldValue("totals.specialfee_subtotal", 0, false);
                                    formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
                                    fetchRemovalOrder(selectedOption.value);
                                    formik.setFieldValue("order_number", selectedOption.label);
                                    formik.setFieldValue("installation_order", selectedOption.value);
                                    const id = await ensureDraftUniqueId(token || "");
                                    if (id) {
                                        formik.setFieldValue("draft_unique_id", id, false);
                                    }
                                } else {
                                    setSelectedInstallationOption(null);
                                    setSelectedSpecialFees([]);
                                    formik.setFieldValue("specialfee", [], false);
                                    formik.setFieldValue("totals.specialfee_subtotal", 0, false);
                                    formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
                                    formik.setFieldValue("order_number", "");
                                    formik.setFieldValue("installation_order", "");
                                }
                            }}
                            additional={{ page: 1 }}
                            defaultOptions
                            debounceTimeout={300}
                            placeholder="Search and select an order..."
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                            classNamePrefix="rs"
                        />
                    </div>
                    {/* keep selectedInstallationOption in sync if formik is initialized elsewhere */}
                    {/* ensures the selected option label displays when the form is loaded with values */}
                    {(() => {
                        if (!selectedInstallationOption && formik.values.installation_order && formik.values.order_number) {
                            setSelectedInstallationOption({ value: formik.values.installation_order, label: formik.values.order_number });
                        }
                        return null;
                    })()}

                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userType" className="form-label table-cells">
                            Buyer Type
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="userType"
                            name="userType"
                            value={formik.values.userType}
                            disabled
                            readOnly
                        />
                    </div>

                    {/* Brokerage Firm Name - Don't show for standalone agent orders */}
                    {removalOrder?.brokerage_firm_name && !(formik.values.userType === "agent" && !removalOrder?.office?._id) && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Brokerage Firm
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.brokerage_firm_name || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    {/* Team Member Name */}
                    {removalOrder?.team_member_name && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Team Member
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.team_member_name || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    {/* Office Name */}
                    {removalOrder?.office?.officeName && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Office
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.office?.officeName || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    {/* Agent Name */}
                    {removalOrder?.agent_name && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Agent
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.agent_name || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}
                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userEmail" className="form-label table-headers">Email <span className="text-danger">*</span></label>
                        <input
                            type="email"
                            className={`form-control table-cells ${formik.touched.userEmail && formik.errors.userEmail ? "is-invalid" : ""}`}
                            id="userEmail"
                            name="userEmail"
                            placeholder="Enter email"
                            value={formik.values.userEmail}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.userEmail && typeof formik.errors.userEmail === "string" && (
                            <div className="invalid-feedback d-block">{formik.errors.userEmail}</div>
                        )}
                    </div>

                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userPhone" className="form-label  table-headers">
                            Phone <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className={`form-control table-cells ${formik.touched.userPhone && formik.errors.userPhone ? "is-invalid" : ""}`}
                            id="userPhone"
                            name="userPhone"
                            placeholder="Enter phone"
                            value={formik.values.userPhone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.userPhone && typeof formik.errors.userPhone === "string" && (
                            <div className="invalid-feedback d-block">{formik.errors.userPhone}</div>
                        )}
                    </div>
                </div>

                <div className="d-block d-lg-flex gap-3">
                    <div className="col-lg-8">
                        <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
                            <div className="mb-3">
                                <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
                            </div>
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-cells">
                                    Removal Date
                                </label>
                                <input
                                    type="date"
                                    className={`form-control ${formik.touched.removal_date && formik.errors.removal_date ? "is-invalid" : ""
                                        }`}
                                    id="removal_date"
                                    name="removal_date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.removal_date}
                                />

                                {/* ✅ TS-Safe Error Display */}
                                {formik.touched.removal_date && typeof formik.errors.removal_date === "string" && (
                                    <div className="invalid-feedback">{formik.errors.removal_date}</div>
                                )}
                            </div>
                            {/* Assigned To */}
                            {organizationId ? (
                                <div className="col-lg-4 mb-3">
                                    <label className="form-label table-cells">
                                        Assigned To <span className="text-danger">*</span>
                                    </label>
                                    <AsyncPaginate
                                        id="assignedId"
                                        name="assignedId"
                                        classNamePrefix="rs"
                                        placeholder="Search Assigned To..."
                                        value={selectedSignageTeamMemberOption ? {
                                            value: selectedSignageTeamMemberOption.value,
                                            label: selectedSignageTeamMemberOption.label,
                                            firstName: selectedSignageTeamMemberOption.firstName,
                                            lastName: selectedSignageTeamMemberOption.lastName,
                                            email: selectedSignageTeamMemberOption.email,
                                            phone: selectedSignageTeamMemberOption.phone
                                        } : null}
                                        loadOptions={loadSignageTeamMemberOptions}
                                        onChange={(selected) => {
                                            if (selected) {
                                                formik.setFieldValue("assignedId", selected.value || "");
                                                setSelectedSignageTeamMemberOption(selected);
                                                // Also update the old selectedInstaller for compatibility
                                                setSelectedInstaller({
                                                    value: selected.value,
                                                    label: selected.label,
                                                    firstName: selected.firstName,
                                                    lastName: selected.lastName,
                                                    email: selected.email,
                                                    phone: selected.phone
                                                });
                                            } else {
                                                formik.setFieldValue("assignedId", "");
                                                setSelectedSignageTeamMemberOption(null);
                                                setSelectedInstaller(null);
                                            }
                                        }}
                                        onBlur={() => formik.setFieldTouched("assignedId", true)}
                                        additional={{ page: 1 }}
                                        defaultOptions
                                        debounceTimeout={300}
                                        isClearable
                                        components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                    />
                                    {formik.touched.assignedId && formik.errors.assignedId && (
                                        <div className="text-danger">{formik.errors.assignedId}</div>
                                    )}
                                </div>
                            ) : (
                                <div className="col-lg-4 mb-3">
                                    <label className="form-label table-cells">
                                        Assigned To <span className="text-danger">*</span>
                                    </label>
                                    <div className="form-control" style={{ display: 'flex', alignItems: 'center', color: '#888' }}>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Loading...
                                    </div>
                                </div>
                            )}

                            {/* Jobs Today */}
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-headers">Jobs Today</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id="jobs_today"
                                    name="jobs_today"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={scheduledJobsCount || formik.values.jobs_today}
                                    disabled
                                />
                                {formik.touched.jobs_today && formik.errors.jobs_today && (
                                    <div className="invalid-feedback d-block">{formik.errors.jobs_today}</div>
                                )}
                            </div>

                            {/* ✅ Removal Priority */}
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-cells">
                                    Removal Priority <span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={[
                                        { value: "3", label: "Normal" },
                                        { value: "2", label: "High Priority" },
                                        { value: "1", label: "Critical" },
                                    ]}
                                    value={[
                                        { value: "3", label: "Normal" },
                                        { value: "2", label: "High Priority" },
                                        { value: "1", label: "Critical" },
                                    ].find((option) => option.value === formik.values.priority) || null}
                                    onChange={(option) => formik.setFieldValue("priority", option?.value || "")}
                                    onBlur={() => formik.setFieldTouched("priority", true)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                                {formik.touched.priority && typeof formik.errors.priority === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.priority}</div>
                                )}
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-4 card-body shadow-lg mb-4 ms-0 pb-3 p-3">
                            <div className="col-lg-12 d-flex mb-3">
                                {/* Address: teal */}
                                <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="col-lg-4" />
                                <div className="align-items-baseline col-lg-8 d-flex mb-3 justify-content-end">
                                    <div className="align-items-baseline col-lg-6 d-flex justify-content-end">
                                        <label className="me-2">Gate Code</label>
                                        <input type="text" id="gatecode" name="gatecode" className="form-control w-50" value={formik.values.gatecode} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Enter Gate Code" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-12">
                                <div className="align-items-baseline col-lg-12 d-flex justify-content-end">
                                    {/* <div className="my-2 fw-bold">
                                        <input
                                            type="checkbox"
                                            id="isSameAsBilling"
                                            name="isSameAsBilling"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            checked={formik.values.isSameAsBilling}
                                        />{" "}
                                        Billing and Installation Address are Same

                                    </div> */}
                                    <button
                                        className="btn btn-primary table-addbutton br-8"
                                        type="button"
                                        onClick={() => setShowAddressForm(true)}
                                        // disabled={Boolean(formik.values.billingAddressId)}
                                        title={formik.values.billingAddressId ? "Billing address selected" : "Add new address"}
                                    >
                                        + Address
                                    </button>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6 mb-3">
                                        <label
                                            htmlFor="billingAddressId"
                                            className="form-label table-cells"
                                        >
                                            Billing Address <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            id="billingAddress"
                                            name="billingAddressId"
                                            options={addressOptions}
                                            value={addressOptions?.find((option) => option.value === formik.values.billingAddressId) || null}
                                            onChange={(selected) => {
                                                formik.setFieldValue("billingAddressId", selected?.value || "");
                                                const selectedAddress = addressObjects?.find((addr) => addr._id === selected.value);
                                                if (formik.values.officeId && !formik.values.agentId) {
                                                    setSelectedOfficeBillingAddress(selectedAddress);
                                                    // console.log("selectedOfficeBillingAddress :", selectedAddress);
                                                } else {
                                                    setSelectedBillingAddress(selectedAddress);
                                                    // console.log("selectedAgentBillingAddress :", selectedAddress);
                                                }
                                                formik.setFieldValue("isSameAsBilling", false);
                                            }}
                                            onBlur={() => formik.setFieldTouched("billingAddressId", true)}
                                            components={{ DropdownIndicator }}
                                            styles={customSelectStyles}
                                            classNamePrefix="rs"
                                        />
                                        {formik.touched.billingAddressId &&
                                            typeof formik.errors.billingAddressId === "string" && (
                                                <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                            )}
                                    </div>
                                    {/* {formik.values.isSameAsBilling ? null : ( */}
                                    <div className="col-lg-6 mb-3">
                                        <label
                                            htmlFor="removalAddressId"
                                            className="form-label table-cells"
                                        >
                                            Removal Address <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                installationAddress?.address_details?.addressLine1 ||
                                                    installationAddress?.address_details?.city ||
                                                    installationAddress?.address_details?.state
                                                    ? [
                                                        installationAddress?.address_details?.addressLine1,
                                                        installationAddress?.address_details?.city,
                                                        installationAddress?.address_details?.state
                                                    ].filter(Boolean).join(', ')
                                                    : ''
                                            }
                                            placeholder="Removal Address"
                                            className="form-control"
                                            disabled
                                        />
                                    </div>
                                    {/* )} */}
                                </div>
                            </div>
                            <div className="col-lg-12">
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                    // onClick={handleMapClick}
                                    >
                                        {selectedLocation && <Marker
                                            position={selectedLocation}
                                            draggable={false}
                                        // onDragEnd={handleMarkerDragEnd}
                                        />}
                                    </GoogleMap>
                                </div>
                            </div>
                        </div>
                        <div className="row col-lg-12 mb-5 card-body shadow-lg mb-3 ms-0 p-3">
                            <div className="d-flex mb-3">
                                <span
                                    aria-hidden="true"
                                    className="image-icons"
                                    style={{
                                        backgroundColor: "#9810fa",
                                        WebkitMaskImage: `url(${ProductSelection})`,
                                        maskImage: `url(${ProductSelection})`,
                                    }}
                                />
                                <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                            </div>

                            {/* Desktop Table View */}
                            <div className="col-lg-12 d-none d-lg-block">
                                <table className="border table table-striped">
                                    <thead className="">
                                        <tr>
                                            <th className="fw-medium table-headers text-center" style={{ width: "10%" }}>Name</th>
                                            <th className="fw-medium table-headers text-center" style={{ width: "8%" }}>SKU</th>
                                            <th className="fw-medium table-headers text-center" style={{ width: "10%" }}>installed Qty </th>
                                            <th className="fw-medium table-headers text-center" style={{ width: "8%" }}>Qty to remove </th>
                                            <th className="fw-medium table-headers text-center" style={{ width: "18%" }}>Product Location Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const regularItems = products?.filter((item: any) => {
                                                if (item.inventory_source === "rotational" || item.inventory_source === "custom") return false;
                                                const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                                                const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                                                return availableQuantity > 0;
                                            }) || [];
                                            const customItems = products?.filter((item: any) => item.inventory_source === "custom") || [];
                                            return (
                                                <>
                                                    {regularItems.map((item: any, index: number) => (
                                                        <tr key={`reg-${item.product_id}-${index}`}>
                                                            <td className="text-center table-cells text-ellipsis">{item.product_name}</td>
                                                            <td className="text-center table-cells">{displayRemovalLineItemSku(item)}</td>
                                                            <td className="text-center table-cells">{getAvailableQuantity(item)}</td>
                                                            <td className="text-center table-cells">
                                                                <input
                                                                    type="number"
                                                                    className="form-control text-center table-cells"
                                                                    value={removalQuantities[item.product_id] || ""}
                                                                    placeholder="0"
                                                                    onChange={(e) => {
                                                                        const inputVal = parseInt(e.target.value) || 0;
                                                                        const availableQty = getAvailableQuantity(item);
                                                                        const clampedVal = Math.min(inputVal, availableQty);
                                                                        setRemovalQuantities((prev) => ({
                                                                            ...prev,
                                                                            [item.product_id]: clampedVal,
                                                                        }));
                                                                        if (inputVal > 0) {
                                                                            setQuantityError(false);
                                                                        }
                                                                    }}
                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                />
                                                            </td>
                                                            <td className="text-center table-cells">
                                                                <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {customItems.length > 0 && (
                                                        <>
                                                            <tr>
                                                                <td colSpan={5} className="small fw-semibold py-2" style={{ backgroundColor: "#f8f9fa" }}>
                                                                    Custom Products
                                                                </td>
                                                            </tr>
                                                            {customItems.map((item: any, index: number) => (
                                                                <tr key={`custom-${item.product_id}-${index}`}>
                                                                    <td className="text-center table-cells text-ellipsis">{item.product_name}</td>
                                                                    <td className="text-center table-cells">{displayRemovalLineItemSku(item)}</td>
                                                                    <td className="text-center table-cells">{getAvailableQuantity(item)}</td>
                                                                    <td className="text-center table-cells">
                                                                        <input
                                                                            type="number"
                                                                            className="form-control text-center table-cells"
                                                                            value={removalQuantities[item.product_id] || ""}
                                                                            placeholder="0"
                                                                            onChange={(e) => {
                                                                                const inputVal = parseInt(e.target.value) || 0;
                                                                                const availableQty = getAvailableQuantity(item);
                                                                                const clampedVal = Math.min(inputVal, availableQty);
                                                                                setRemovalQuantities((prev) => ({
                                                                                    ...prev,
                                                                                    [item.product_id]: clampedVal,
                                                                                }));
                                                                                if (inputVal > 0) {
                                                                                    setQuantityError(false);
                                                                                }
                                                                            }}
                                                                            onWheel={(e) => e.currentTarget.blur()}
                                                                        />
                                                                    </td>
                                                                    <td className="text-center table-cells">
                                                                        <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile/Tablet Card View */}
                            <div className="col-lg-12 d-lg-none">
                                <div className="row g-3">
                                    {(() => {
                                        const regularItems = products?.filter((item: any) => {
                                            if (item.inventory_source === "rotational" || item.inventory_source === "custom") return false;
                                            const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                                            const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                                            return availableQuantity > 0;
                                        }) || [];
                                        const customItems = products?.filter((item: any) => item.inventory_source === "custom") || [];
                                        return (
                                            <>
                                                {regularItems.map((item: any, index: number) => (
                                                    <div key={`reg-${item.product_id}-${index}`} className="col-12 mb-3">
                                                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                            <div className="card-body p-3">
                                                                <div className="mb-3">
                                                                    <h6>Product Name</h6>
                                                                    <h6 className="card-title mb-0 fw-medium">{item.product_name}</h6>
                                                                </div>
                                                                <div className="row g-2 mb-3">
                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Installed Qty</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center"
                                                                            value={getAvailableQuantity(item)}
                                                                            readOnly
                                                                            style={{ backgroundColor: '#f8f9fa' }}
                                                                        />
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Qty to Remove</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center"
                                                                            value={removalQuantities[item.product_id] || ""}
                                                                            placeholder="0"
                                                                            min="0"
                                                                            max={getAvailableQuantity(item)}
                                                                            onChange={(e) => {
                                                                                const inputVal = parseInt(e.target.value) || 0;
                                                                                const availableQty = getAvailableQuantity(item);
                                                                                const clampedVal = Math.min(inputVal, availableQty);
                                                                                setRemovalQuantities((prev) => ({
                                                                                    ...prev,
                                                                                    [item.product_id]: clampedVal,
                                                                                }));
                                                                                if (inputVal > 0) setQuantityError(false);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="row g-2">
                                                                    <div className="col-6">
                                                                        <h6>SKU</h6>
                                                                        <span className="fw-medium">{displayRemovalLineItemSku(item)}</span>
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <h6>Product Location Status</h6>
                                                                        <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {customItems.length > 0 && (
                                                    <>
                                                        <div className="col-12">
                                                            <h6 className="small fw-semibold text-muted mb-2">Custom Product</h6>
                                                        </div>
                                                        {customItems.map((item: any, index: number) => (
                                                            <div key={`custom-${item.product_id}-${index}`} className="col-12 mb-3">
                                                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                                    <div className="card-body p-3">
                                                                        <div className="mb-3">
                                                                            <h6>Product Name</h6>
                                                                            <h6 className="card-title mb-0 fw-medium">{item.product_name}</h6>
                                                                        </div>
                                                                        <div className="row g-2 mb-3">
                                                                            <div className="col-6">
                                                                                <label className="form-label small mb-1">Installed Qty</label>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm text-center"
                                                                                    value={getAvailableQuantity(item)}
                                                                                    readOnly
                                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                                />
                                                                            </div>
                                                                            <div className="col-6">
                                                                                <label className="form-label small mb-1">Qty to Remove</label>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm text-center"
                                                                                    value={removalQuantities[item.product_id] || ""}
                                                                                    placeholder="0"
                                                                                    min="0"
                                                                                    max={getAvailableQuantity(item)}
                                                                                    onChange={(e) => {
                                                                                        const inputVal = parseInt(e.target.value) || 0;
                                                                                        const availableQty = getAvailableQuantity(item);
                                                                                        const clampedVal = Math.min(inputVal, availableQty);
                                                                                        setRemovalQuantities((prev) => ({
                                                                                            ...prev,
                                                                                            [item.product_id]: clampedVal,
                                                                                        }));
                                                                                        if (inputVal > 0) setQuantityError(false);
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="row g-2">
                                                                            <div className="col-6">
                                                                                <h6>SKU</h6>
                                                                                <span className="fw-medium">{displayRemovalLineItemSku(item)}</span>
                                                                            </div>
                                                                            <div className="col-6">
                                                                                <h6>Product Location Status</h6>
                                                                                <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-3">
                            <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                                <InstructionsCreateCard
                                    title="Instructions"
                                    // mode="instructions_files"
                                    loginType="signage"
                                    value={formik.values.instructions}
                                    onChange={(v) => formik.setFieldValue("instructions", v, false)}
                                    placeholder="Add instructions here"
                                    rows={4}
                                    className="mt-3"
                                    iconUrl={OrderNotes}
                                    iconColor="#f54a00"
                                    iconBackground="#EAF7F1"
                                />
                            </div>
                            <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                                <div className="align-items-center d-flex mb-3">
                                    <span
                                        aria-hidden="true"
                                        className="image-icons"
                                        style={{
                                            backgroundColor: "#00a63e",
                                            WebkitMaskImage: `url(${Doller})`,
                                            maskImage: `url(${Doller})`,
                                        }}
                                    />
                                    <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                                </div>
                                {effectiveCouponCreatedById && (
                                    <OrderDiscountCodeField
                                        className="mb-2"
                                        value={couponInput}
                                        appliedCode={liveDiscountDisplay.code}
                                        discountAmount={liveDiscountDisplay.amount}
                                        disabled={isRecomputing}
                                        onChange={setCouponInput}
                                        onApply={(code: string) => {
                                            const trimmed = code.trim();
                                            if (!trimmed) return;
                                            userClearedCouponRef.current = false;
                                            setCouponInput(trimmed);
                                            formik.setFieldValue("discount_code", trimmed, false);
                                            void recompute({ force: true, discountCode: trimmed });
                                        }}
                                        onClear={() => {
                                            userClearedCouponRef.current = true;
                                            setCouponInput("");
                                            formik.setFieldValue("discount_code", "", false);
                                            formik.setFieldValue("discount_amount", 0, false);
                                            setZeroOrNegativeMsg("");
                                            void recompute({ force: true, discountCode: "" });
                                        }}
                                    />
                                )}
                                {zeroOrNegativeMsg && <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>}
                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Total Units:</span>
                                    <span className="fw-semibold">{Object.values(removalQuantities).reduce((sum, qty) => sum + qty, 0)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Removal Cost:</span>
                                    <span className="fw-semibold">${Number(removalCost).toFixed(2)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Sub-total:</span>
                                    <span className="fw-semibold">${(
                                        Number(removalCost || 0)
                                        // Number(formik.values.totals.specialfee_subtotal || 0) -
                                        // Number(formik.values?.discount_amount || 0)
                                    ).toFixed(2)}</span>
                                </div>

                                {Number(formik.values?.discount_amount || 0) > 0 && (
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="fw-medium" style={{ color: "#00a63e" }}>Discount ({formik.values?.discount_code}):</span>
                                        <span className="fw-semibold text-success" style={{ color: "#00a63e" }}>
                                            -${Number(formik.values?.discount_amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    {(() => {
                                        const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                                        return (
                                            <>
                                                <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                                                    {labelText}:
                                                </span>
                                                <span className="fw-semibold flex-shrink-0">${Number(formik.values.totals.specialfee_subtotal || 0).toFixed(2)}</span>
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="py-1">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowTaxDetails(v => !v)}
                                            className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                                            aria-expanded={showTaxDetails}
                                            aria-controls="tax-breakdown"
                                        >
                                            {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                                            <span className="fw-medium">Tax:</span>
                                        </button>
                                        <span className="fw-semibold">
                                            ${(
                                                Number(formik.values.totals.installation_tax_amount || 0) +
                                                Number(formik.values.totals.specialfee_tax_amount || 0)
                                            ).toFixed(2)}
                                        </span>
                                    </div>

                                    {/* {showTaxDetails && (
                                        <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>Removal Tax</span>
                                                <span>${Number(formik.values.totals.installation_tax_amount || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span>Special Fee Tax</span>
                                                <span>${Number(formik.values.totals.specialfee_tax_amount || 0).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )} */}
                                </div>

                                <hr className="my-3" />

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-bold fs-17">Total Amount:</span>
                                    <span className="fw-bold fs-17 text-primary">${Number(formik.values.totals.grand_total || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3 col-lg-12 rcust-margin-4 mt-4">
                            <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                                {/* Left: Back */}
                                <div className="col-lg-2">
                                    <button
                                        className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                        type="button"
                                        onClick={() => safeNavigate(-1)}
                                    >
                                        <i className="bi bi-chevron-left me-2" />
                                        Back
                                    </button>
                                </div>
                                {/* Right: checkbox + submit */}
                                <div className="d-block d-lg-flex align-items-baseline justify-content-end">
                                    <div className="col-lg-10 mt-3">
                                        <div className="d-flex d-lg-block form-check justify-content-center mb-3 mb-lg-0 gap-3">
                                            <input
                                                type="checkbox"
                                                id="localLaws"
                                                name="localLawsChecked"
                                                className="form-check-input"
                                                checked={formik.values.localLawsChecked}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <label htmlFor="localLaws" className="form-check-label mb-0 text-nowrap">
                                                I Acknowledge to local laws
                                            </label>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 w-100"
                                        disabled={formSubmitting || isRecomputing || !formik.values.localLawsChecked}
                                    >
                                        {isRecomputing ? "Calculating..." : "Save Draft"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {removalSpecialFeeSupplierId && effectiveCouponCreatedById && (
                    <div className="col-lg-4">
                        <PONumber
                            mode="create"
                            className="mb-3"
                            value={String(formik.values.po_number || "")}
                            onChange={(v) => formik.setFieldValue("po_number", v)}
                        />
                        <SpecialFeeui
                            token={token!}
                            personId={effectiveCouponCreatedById}
                            supplierId={removalSpecialFeeSupplierId}
                            order={removalOrder}
                            orderId={String(removalOrder?._id || removalOrder?.id || id || "").trim()}
                            orderType="installation"
                            formik={formik}
                            name="specialfee"
                            apiBase={process.env.REACT_APP_API_URL || ""}
                            readOnly={false}
                            autoSelectRequired={false}
                            disabled={isRecomputing}  // ✅ Disable special fee selection when recompute is running
                            title="Special Fees"
                            onOptionsLoaded={(allOptions: any) => {
                                setAllAvailableSpecialFees(allOptions);
                                // console.log("📋 All available special fees loaded:", allOptions);
                            }}
                            onUserChange={(nextFees: any) => {
                                if (isRecomputingRef.current) return;
                                isUpdatingFromRecomputeRef.current = false;
                                setSelectedSpecialFees(nextFees);
                            }}
                        />
                    </div>
                    )}
                </div>
                {showAddressForm && (

                    <AddressFormModal
                        isOpen={true}
                        onClose={() => {
                            setShowAddressForm(false);
                        }}
                        token={token || ""}
                        owner={ownerOption}
                        useType="billing"
                         userType={
                      formik.values.userType === "brokerageFirm"
                        ? selectedOfficeAgent
                          ? "agent"
                          : selectedTeamMember || selectedOffice
                            ? "office"
                            : undefined
                        : formik.values.userType === "agent"
                          ? "agent"
                          : selectedOfficeAgent || selectedAgent
                            ? "agent"
                            : selectedOffice
                              ? "office"
                              : undefined
                    }
                        prefill={{
                            label_of_address: formik?.values?.label_of_address || "",
                            firstName: formik?.values?.firstName || "",
                            lastName: formik?.values?.lastName || "",
                            company: formik?.values?.company || "",
                            phone: formik?.values?.phone || "",
                            addressLine1: formik?.values?.addressLine1 || "",
                            addressLine2: formik?.values?.addressLine2 || "",
                            city: formik?.values?.city || "",
                            state: formik?.values?.state || "",
                            zipcode: formik?.values?.zipcode || "",
                            country: formik?.values?.country || "",
                        }}
                        onCreated={(createdRaw, option, useType) => {
                            // Add the new address to the address list
                            setAddressObjects((prev) => [createdRaw, ...(prev || [])]);

                            // Set the billing address field value
                            formik.setFieldValue(
                                useType === "billing" ? "billingAddressId" : "shippingAddressId",
                                option.value
                            );

                            // Determine if this is an office order (no agent, only office)
                            const isOffice = !!selectedOffice && !selectedOfficeAgent && !selectedAgent;

                            // Only update billing address state, NOT installation/removal address
                            // Removal address should always remain as the installation order's address
                            if (useType === "billing") {
                                if (isOffice) {
                                    setSelectedOfficeBillingAddress(createdRaw);
                                } else {
                                    setSelectedBillingAddress(createdRaw);
                                }
                            }

                            // Note: We don't update installationAddress here because it should
                            // always be the address from the installation order
                        }}
                        afterCreateRefresh={() => {
                            // Call refreshers based on selected user priority
                            if (selectedOfficeAgent) return fetchUserAddresses(selectedOfficeAgent.value);
                            if (selectedAgent) return fetchUserAddresses(selectedAgent.value);
                            if (selectedOffice) return fetchUserAddresses(selectedOffice.value);
                        }}
                    />
                )}
            </form>
        </div>
    );
};

export default SignageAddRemovalRequest;
